import React, { useEffect, useMemo, useState } from "react";

const BRAND = "#015b9a";
const MAX_TABLES = 50;
// Prevent massive/invalid ranges when rendering trim number grid
const TRIM_GRID_LIMIT = 1000;

const AddTablePage = () => {
  const [user, setUser] = useState(null);
  const [tableList, setTableList] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customType, setCustomType] = useState("table");
  const [trimOpen, setTrimOpen] = useState(false);
  const [trimSubmitting, setTrimSubmitting] = useState(false);
  const [selectedTrimNumber, setSelectedTrimNumber] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deletingTable, setDeletingTable] = useState(false);
  const [editTable, setEditTable] = useState(null);
  const [editName, setEditName] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editCapacity, setEditCapacity] = useState("0");
  const [editType, setEditType] = useState("table");

  /* ------------------------------ bootstrap ------------------------------ */
  useEffect(() => {
    const stored = localStorage.getItem("userData");
    if (stored) setUser(JSON.parse(stored)?.data || null);
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchTables(user?.id);
  }, [user]);

  /* --------------------------------- utils -------------------------------- */
  const getTableKey = (table) =>
    String(
      table?.table_key ??
        table?.tableKey ??
        table?.key ??
        table?.name ??
        table?.table ??
        table?.tableid ??
        table?.tableId ??
        table?.id ??
        ""
    ).trim();

  const getTableDisplayName = (table) =>
    String(
      table?.display_name ??
        table?.displayName ??
        table?.name ??
        table?.table ??
        table?.table_key ??
        table?.tableKey ??
        ""
    ).trim();

  const getTableType = (table) => {
    const raw = String(
      table?.table_type ?? table?.tableType ?? table?.type ?? "table"
    ).toLowerCase();
    return raw === "room" ? "room" : "table";
  };

  const normalizeTableKey = (raw = "") => String(raw).trim().replace(/\s+/g, " ");

  const toast = (msg, type = "info") => {
    let root = document.getElementById("toast-root");
    if (!root) {
      root = document.createElement("div");
      root.id = "toast-root";
      Object.assign(root.style, {
        position: "fixed",
        right: 16,
        bottom: 16,
        display: "grid",
        gap: 8,
        zIndex: 1000,
      });
      document.body.appendChild(root);
    }
    const el = document.createElement("div");
    el.textContent = msg;
    Object.assign(el.style, {
      padding: "10px 14px",
      borderRadius: 12,
      color: "#0f172a",
      fontWeight: 800,
      border: "1px solid rgba(15,23,42,.08)",
      backdropFilter: "blur(10px)",
      boxShadow: "0 18px 38px rgba(15,23,42,.25)",
      background:
        type === "success"
          ? "linear-gradient(135deg,#a8ff78,#78ffd6)"
          : type === "error"
          ? "linear-gradient(135deg,#ffd1d1,#ffe3e3)"
          : "linear-gradient(135deg,#e0f3ff,#bff3ff)",
    });
    root.appendChild(el);
    setTimeout(() => {
      el.style.transition = "all .35s";
      el.style.opacity = 0;
      el.style.transform = "translateY(8px)";
      setTimeout(() => el.remove(), 380);
    }, 2200);
  };

  /* ------------------------------- derived ------------------------------- */
  const numericTableNames = useMemo(() => {
    return (tableList || [])
      .map((item) => parseInt(getTableKey(item), 10))
      .filter((val) => Number.isFinite(val) && val > 0);
  }, [tableList]);

  const maxTableNumber = useMemo(() => {
    if (!numericTableNames.length) return 0;
    return Math.max(...numericTableNames);
  }, [numericTableNames]);

  const safeTrimLength = useMemo(() => {
    if (!maxTableNumber) return 0;
    const bounded = Math.min(Math.floor(maxTableNumber), TRIM_GRID_LIMIT);
    return bounded > 0 ? bounded : 0;
  }, [maxTableNumber]);

  const trimNumbers = useMemo(() => {
    if (!safeTrimLength) return [];
    return Array.from({ length: safeTrimLength }, (_, idx) => idx + 1);
  }, [safeTrimLength]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tableList;
    return (tableList || []).filter((t) => {
      const name = String(getTableDisplayName(t) || "").toLowerCase();
      const key = String(getTableKey(t) || "").toLowerCase();
      const note = String(t?.notes || "").toLowerCase();
      return name.includes(q) || key.includes(q) || note.includes(q);
    });
  }, [tableList, search]);

  const tableItems = useMemo(
    () => filtered.filter((t) => getTableType(t) !== "room"),
    [filtered]
  );

  const roomItems = useMemo(
    () => filtered.filter((t) => getTableType(t) === "room"),
    [filtered]
  );

  const limitReached = useMemo(() => (tableList || []).length >= MAX_TABLES, [tableList]);

  /* --------------------------------- api --------------------------------- */
  const fetchTables = async (restaurantid) => {
    if (!restaurantid) return;
    setLoading(true);
    try {
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/list/table",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid }),
        }
      );
      const result = await res.json();
      if (res.ok) setTableList(result.list || []);
      else toast(result.message || "Failed to fetch tables", "error");
    } catch (e) {
      toast(e?.message || "Network error", "error");
    } finally {
      setLoading(false);
    }
  };

  const submitCustomTable = async () => {
    if (!user?.id) {
      toast("Missing restaurant details", "error");
      return;
    }

    const rawKey = normalizeTableKey(customName);
    if (!rawKey) {
      toast("Enter table name/number", "error");
      return;
    }

    const keyLower = rawKey.toLowerCase();
    const exists = (tableList || []).some((item) => {
      const existingKey = normalizeTableKey(getTableKey(item)).toLowerCase();
      const existingName = normalizeTableKey(getTableDisplayName(item)).toLowerCase();
      return existingKey === keyLower || existingName === keyLower;
    });
    if (exists) {
      toast("Table name already exists", "error");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        restaurantid: user?.id,
        capacity: 0,
        table_key: rawKey,
        display_name: customName.trim(),
        table_type: customType,
      };
      const response = await fetch(
        "https://qr.billsarthi.in/public/api/add/tablenew",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.message || "Failed to add table");
      }

      toast("Table added successfully", "success");
      setAddOpen(false);
      setCustomName("");
      setCustomType("table");
      await fetchTables(user?.id);
    } catch (e) {
      toast(e?.message || "Network error", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const trimTablesTo = async (keepNumber) => {
    if (!user?.id) {
      toast("Missing restaurant details", "error");
      return;
    }
    const numericEntries = (tableList || [])
      .map((item) => {
        const key = getTableKey(item);
        const num = parseInt(key, 10);
        return Number.isNaN(num) ? null : { num, item };
      })
      .filter((entry) => entry !== null);

    if (!keepNumber || !numericEntries.length) {
      toast("No tables to trim", "error");
      return;
    }

    const tablesAbove = numericEntries
      .filter((entry) => entry.num > keepNumber)
      .sort((a, b) => a.num - b.num);
    let tablesToDelete = tablesAbove;

    if (tablesToDelete.length === 0) {
      const maxTable = Math.max(...numericEntries.map((entry) => entry.num));
      if (keepNumber === maxTable) {
        const selfEntry = numericEntries.find((entry) => entry.num === maxTable);
        tablesToDelete = selfEntry ? [selfEntry] : [];
      } else {
        toast("Already within range", "success");
        setTrimOpen(false);
        setSelectedTrimNumber(null);
        return;
      }
    }

    try {
      setTrimSubmitting(true);
      for (let i = 0; i < tablesToDelete.length; i++) {
        const entry = tablesToDelete[i];
        const tableObj = entry?.item;
        if (!tableObj?.id) continue;
        const payload = {
          restaurantid: user?.id,
          tableid: tableObj.id,
        };
        if (tableObj?.table_key) payload.table_key = tableObj.table_key;
        if (tableObj?.name) payload.name = tableObj.name;

        const response = await fetch(
          "https://qr.billsarthi.in/public/api/delete/table",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result?.message || "Failed to delete table");
        }
      }

      toast("Tables trimmed successfully", "success");
      setTrimOpen(false);
      setSelectedTrimNumber(null);
      await fetchTables(user?.id);
    } catch (e) {
      toast(e?.message || "Network error", "error");
    } finally {
      setTrimSubmitting(false);
    }
  };

  const deleteTableById = async (table) => {
    if (!table?.id) {
      toast("Missing table details", "error");
      return;
    }
    const ok = window.confirm("Are you sure you want to delete this table?");
    if (!ok) return;
    try {
      setDeletingTable(true);
      const payload = {
        restaurantid: user?.id,
        tableid: table.id,
      };
      if (table?.table_key) payload.table_key = table.table_key;
      if (table?.name) payload.name = table.name;
      const response = await fetch("https://qr.billsarthi.in/public/api/delete/table", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.message || "Failed to delete table");
      }
      toast("Table deleted successfully", "success");
      closeEdit();
      await fetchTables(user?.id);
    } catch (e) {
      toast(e?.message || "Network error", "error");
    } finally {
      setDeletingTable(false);
    }
  };

  const openEdit = (item) => {
    setEditTable(item);
    setEditName(String(getTableDisplayName(item) || ""));
    setEditNotes(item?.notes || "");
    const seat = item?.seatcapacity ?? 0;
    setEditCapacity(
      String(Number.isNaN(parseInt(seat, 10)) ? 0 : parseInt(seat, 10))
    );
    setEditType(getTableType(item));
    setEditOpen(true);
  };

  const closeEdit = () => {
    if (!editSubmitting) {
      setEditOpen(false);
      setEditTable(null);
      setEditName("");
      setEditNotes("");
      setEditCapacity("0");
      setEditType("table");
    }
  };

  const submitEdit = async () => {
    if (!user?.id || !editTable?.id) {
      toast("Missing table details", "error");
      return;
    }
    const nameVal = editName.trim();
    if (!nameVal) {
      toast("Enter table name", "error");
      return;
    }

    const capacityVal = editCapacity.trim();
    const parsedCap = capacityVal === "" ? 0 : parseInt(capacityVal, 10);
    if (Number.isNaN(parsedCap) || parsedCap < 0) {
      toast("Enter a valid seat capacity", "error");
      return;
    }

    try {
      setEditSubmitting(true);
      const payload = {
        restaurantid: user?.id,
        tableid: editTable?.id,
        display_name: nameVal,
        table_type: editType,
        capacity: parsedCap,
      };
      if (editTable?.name !== undefined && editTable?.name !== null) {
        payload.name = editTable.name;
      }

      const noteVal = editNotes.trim();
      if (noteVal) {
        payload.notes = noteVal;
      }

      const response = await fetch(
        "https://qr.billsarthi.in/public/api/update/table",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      if (response.ok) {
        toast("Table updated successfully", "success");
        closeEdit();
        await fetchTables(user?.id);
      } else {
        toast(result?.message || "Failed to update table", "error");
      }
    } catch (e) {
      toast(e?.message || "Network error", "error");
    } finally {
      setEditSubmitting(false);
    }
  };

  const renderTrimInfo = () => {
    if (selectedTrimNumber === null) return null;
    const tablesToDeleteList = numericTableNames.filter(
      (n) => n > selectedTrimNumber
    );
    const maxTable =
      numericTableNames.length > 0 ? Math.max(...numericTableNames) : null;
    const isDeletingLastTable =
      selectedTrimNumber === maxTable && tablesToDeleteList.length === 0;
    const tablesToShow =
      tablesToDeleteList.length > 0
        ? tablesToDeleteList
        : isDeletingLastTable
        ? [selectedTrimNumber]
        : [];

    return (
      <div style={sx.selectedInfo}>
        <div style={sx.selectedInfoText}>
          {isDeletingLastTable
            ? `Delete table ${selectedTrimNumber}`
            : `Tables above ${selectedTrimNumber} will be deleted`}
        </div>
        {tablesToShow.length > 0 && (
          <div style={sx.selectedInfoSubtext}>
            Tables to delete: {tablesToShow.join(", ")}
          </div>
        )}
      </div>
    );
  };

  const seatValue = (item) => {
    if (item?.seatcapacity === 0 || item?.seatcapacity === "0") return 0;
    return item?.seatcapacity ?? "-";
  };

  const renderCards = (items) =>
    items.map((t) => (
      <article
        key={t.id}
        style={sx.card}
        className="table-card addtable-card"
      >
        <button
          style={sx.editBadge}
          className="btn-icon"
          onClick={() => openEdit(t)}
          title="Edit"
        >
          Edit
        </button>
        <div style={sx.cardHead}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div style={sx.avatar}>{t?.name}</div>
            <div>
              <div style={sx.cardTitle}>{getTableDisplayName(t)}</div>
              {getTableType(t) === "room" ? (
                <div style={sx.typeBadge}>Room</div>
              ) : null}
            </div>
          </div>
        </div>

        <div style={sx.metaRow} className="addtable-meta">
          <div style={sx.metaBlock}>
            <div style={sx.metaLabel}>Seats</div>
            <div style={sx.metaValue}>{seatValue(t)}</div>
          </div>
          <div style={sx.metaBlockMuted}>
            <div style={sx.metaLabel}>Status</div>
            <div style={sx.metaChip}>Active</div>
          </div>
        </div>

        {t?.notes ? (
          <div style={sx.noteBox}>
            <span style={sx.noteLabel}>Notes</span>
            <span>{t.notes}</span>
          </div>
        ) : null}

        <div style={sx.cardActions}>
          <button
            style={sx.smallEditBtn}
            className="btn-icon"
            onClick={() => openEdit(t)}
            disabled={deletingTable || editSubmitting}
          >
            Edit
          </button>
          <button
            style={sx.smallDeleteBtn}
            className="btn-danger"
            onClick={() => deleteTableById(t)}
            disabled={deletingTable || editSubmitting}
          >
            Delete
          </button>
        </div>
      </article>
    ));

  /* -------------------------------- render -------------------------------- */
  return (
    <div style={sx.page}>
      {/* BG blobs */}
      <div style={sx.bg} aria-hidden>
        <div style={{ ...sx.blob, ...sx.b1 }} />
        <div style={{ ...sx.blob, ...sx.b2 }} />
        <div style={{ ...sx.blob, ...sx.b3 }} />
      </div>

      {/* Content wrap */}
      <main style={sx.main} className="addtable-main">
        <section style={sx.topCard} className="addtable-hero">
          <div style={sx.headerLine} className="addtable-header">
            <div style={sx.statGroup} className="addtable-stat">
              <div style={sx.statPill} className="addtable-statpill">
                Total Tables
                <strong style={{ marginLeft: 6 }}>{tableList.length}</strong>
              </div>
              {tableList.length > 0 && (
                <div
                  style={sx.statPillSecondary}
                  className="addtable-statpill secondary"
                >
                  {limitReached ? (
                    <>Limit {MAX_TABLES} reached</>
                  ) : (
                    <>
                      Active Tables
                      <strong style={{ marginLeft: 6 }}>{tableList.length}</strong>
                    </>
                  )}
                </div>
              )}
            </div>
            <div style={sx.quickActionsRow} className="addtable-actions">
               <button
                style={sx.pillBtn}
                className="btn-pill"
                onClick={() => {
                  if (limitReached) {
                    toast(`Table limit ${MAX_TABLES} reached`, "error");
                    return;
                  }
                  setCustomName("");
                  setCustomType("table");
                  setAddOpen(true);
                }}
              >
                Add Table
              </button>
              {tableList.length > 0 ? (
                <button
                  style={{ ...sx.pillBtn, background: "red",color: "white", }}
                  className="btn-pill"
                  onClick={() => {
                    setSelectedTrimNumber(null);
                    setTrimOpen(true);
                  }}
                >
                  Delete Tables
                </button>
              ) : null}
             
            </div>
          </div>

          {/* <div style={sx.searchRow}>
            <span style={{ fontSize: 18 }}>Search</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search table name or notes"
              style={sx.searchInput}
            />
            <button
              style={sx.refreshBtn}
              className="btn-icon"
              onClick={() => fetchTables(user?.id)}
            >
              Refresh
            </button>
          </div> */}
        </section>

        {/* Grid */}
        <section style={sx.gridWrap}>
          {loading ? (
            <div style={sx.loaderWrap}>
              <div style={sx.spinner} />
              <div style={{ marginTop: 8, opacity: 0.9 }}>Loading tables...</div>
            </div>
          ) : filtered.length === 0 ? (
            <div style={sx.emptyWrap}>
              <div style={sx.emptyCard}>
                <div style={sx.emptyIcon}>Tables</div>
                <div style={sx.emptyTitle}>No tables found</div>
                <p style={sx.emptyText}>
                  Create your first table to start managing orders and seating.
                </p>
                <button
                  style={{ ...sx.primaryBtn, maxWidth: 220 }}
                  className="btn-primary"
                  onClick={() => {
                    if (limitReached) {
                      toast(`Table limit ${MAX_TABLES} reached`, "error");
                      return;
                    }
                    setCustomName("");
                    setCustomType("table");
                    setAddOpen(true);
                  }}
                >
                  Add Table
                </button>
              </div>
            </div>
          ) : (
            <div style={sx.sectionsWrap}>
              <div style={sx.sectionHeader}>
                <h3 style={sx.sectionTitle}>Tables</h3>
                <span style={sx.sectionCount}>{tableItems.length}</span>
              </div>
              {tableItems.length ? (
                <div style={sx.grid} className="addtable-grid">
                  {renderCards(tableItems)}
                </div>
              ) : (
                <div style={sx.sectionEmpty}>No tables found.</div>
              )}

              <div style={{ ...sx.sectionHeader, marginTop: 14 }}>
                <h3 style={sx.sectionTitle}>Rooms</h3>
                <span style={sx.sectionCount}>{roomItems.length}</span>
              </div>
              {roomItems.length ? (
                <div style={sx.grid} className="addtable-grid">
                  {renderCards(roomItems)}
                </div>
              ) : (
                <div style={sx.sectionEmpty}>No rooms found.</div>
              )}
            </div>
          )}
        </section>
      </main>

      {/* Add bottom sheet */}
      {addOpen && (
        <div
          style={sx.centerBackdrop}
          onClick={() => !submitting && setAddOpen(false)}
        >
          <div style={sx.centerModal} onClick={(e) => e.stopPropagation()}>
            <div style={sx.grabber} />
            <div style={sx.sheetHeader}>
              <div style={sx.sheetTitle}>Add Table</div>
              <button
                onClick={() => !submitting && setAddOpen(false)}
                disabled={submitting}
                style={{
                  ...sx.iconBtn,
                  opacity: submitting ? 0.6 : 1,
                }}
                className="btn-icon"
                title="Close"
              >
                Close
              </button>
            </div>

            <p style={sx.sheetHint}>
              Add custom table name/number and choose type.
            </p>

            <div style={sx.formGroup}>
              <label style={sx.label}>Table Name / Number</label>
              <input
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="e.g. Table 1 or Room A"
                style={sx.input}
                maxLength={30}
                disabled={submitting}
              />
            </div>

            <div style={sx.formGroup}>
              <label style={sx.label}>Table Type</label>
              <div style={sx.typeRow}>
                <button
                  style={{
                    ...sx.typeChip,
                    ...(customType === "table" ? sx.typeChipActive : null),
                  }}
                  onClick={() => setCustomType("table")}
                  disabled={submitting}
                >
                  Table
                </button>
                <button
                  style={{
                    ...sx.typeChip,
                    ...(customType === "room" ? sx.typeChipActive : null),
                  }}
                  onClick={() => setCustomType("room")}
                  disabled={submitting}
                >
                  Room
                </button>
              </div>
            </div>

            <button
              onClick={submitCustomTable}
              disabled={submitting}
              style={{
                ...sx.primaryBtn,
                opacity: submitting ? 0.7 : 1,
              }}
              className="btn-primary"
            >
              {submitting ? "Saving..." : "Save Table"}
            </button>
          </div>
        </div>
      )}

      {/* Trim Tables bottom sheet */}
      {trimOpen && (
        <div
          style={sx.centerBackdrop}
          onClick={() => !trimSubmitting && setTrimOpen(false)}
        >
          <div style={sx.centerModal} onClick={(e) => e.stopPropagation()}>
            <div style={sx.grabber} />
            <div style={sx.sheetHeader}>
              <div style={sx.sheetTitle}>Delete Tables</div>
              <button
                onClick={() => !trimSubmitting && setTrimOpen(false)}
                style={{ ...sx.iconBtn, opacity: trimSubmitting ? 0.6 : 1 }}
                className="btn-icon"
              >
                Close
              </button>
            </div>
            <p style={sx.sheetHint}>
              Select the highest table number you want to keep.
            </p>

            {trimNumbers.length ? (
              <>
                <div style={sx.gridPanel}>
                  <div style={sx.numberGrid} className="addtable-numbergrid">
                    {trimNumbers.map((num) => {
                      const disabled = trimSubmitting;
                      const isSelected = selectedTrimNumber === num;
                      const isTableToDelete =
                        selectedTrimNumber !== null &&
                        num > selectedTrimNumber &&
                        numericTableNames.includes(num);
                      const shouldHighlight = isSelected || isTableToDelete;
                      return (
                        <button
                          key={num}
                          style={{
                            ...sx.tableTile,
                            ...(shouldHighlight ? sx.tableTileActive : null),
                            ...(disabled ? sx.tableTileDisabled : null),
                          }}
                          disabled={disabled}
                          onClick={() => setSelectedTrimNumber(num)}
                        >
                          <span
                            style={{
                              ...sx.tableTileText,
                              ...(shouldHighlight
                                ? sx.tableTileTextActive
                                : null),
                              ...(disabled ? sx.tableTileTextDisabled : null),
                            }}
                          >
                            {num}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {renderTrimInfo()}
                <button
                  style={{
                    ...sx.dangerBtn,
                    opacity:
                      !selectedTrimNumber || trimSubmitting ? 0.7 : 1,
                  }}
                  onClick={() => trimTablesTo(selectedTrimNumber)}
                  disabled={!selectedTrimNumber || trimSubmitting}
                  className="btn-danger"
                >
                  {trimSubmitting ? "Deleting..." : "Delete Tables"}
                </button>
              </>
            ) : (
              <div style={sx.emptyText}>No tables to trim</div>
            )}

            <button
              style={{ ...sx.secondaryBtn, opacity: trimSubmitting ? 0.7 : 1 }}
              onClick={() => setTrimOpen(false)}
              disabled={trimSubmitting}
              className="btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Edit Table bottom sheet */}
      {editOpen && (
        <div style={sx.centerBackdrop} onClick={closeEdit}>
          <div style={sx.centerModal} onClick={(e) => e.stopPropagation()}>
            <div style={sx.grabber} />
            <div style={sx.sheetHeader}>
              <div style={sx.sheetTitle}>Edit Table</div>
              <button
                onClick={closeEdit}
                disabled={editSubmitting}
                style={{ ...sx.iconBtn, opacity: editSubmitting ? 0.6 : 1 }}
                className="btn-icon"
              >
                Close
              </button>
            </div>

            <p style={sx.sheetHint}>Update table name, capacity, and notes.</p>

            <div style={sx.formGroup}>
              <label style={sx.label}>Table Name</label>
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Enter table name"
                style={sx.input}
                maxLength={30}
                disabled={editSubmitting}
              />
            </div>

            <div style={sx.formGroup}>
              <label style={sx.label}>Table Type</label>
              <div style={sx.typeRow}>
                <button
                  style={{
                    ...sx.typeChip,
                    ...(editType === "table" ? sx.typeChipActive : null),
                  }}
                  onClick={() => setEditType("table")}
                  disabled={editSubmitting}
                >
                  Table
                </button>
                <button
                  style={{
                    ...sx.typeChip,
                    ...(editType === "room" ? sx.typeChipActive : null),
                  }}
                  onClick={() => setEditType("room")}
                  disabled={editSubmitting}
                >
                  Room
                </button>
              </div>
            </div>

            <div style={sx.formGroup}>
              <label style={sx.label}>Seat Capacity</label>
              <input
                value={editCapacity}
                onChange={(e) =>
                  setEditCapacity(e.target.value.replace(/[^0-9]/g, ""))
                }
                placeholder="Enter seat capacity"
                style={sx.input}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={3}
              />
            </div>

            <div style={sx.formGroup}>
              <label style={sx.label}>Notes</label>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Add optional notes"
                rows={3}
                maxLength={120}
                style={{ ...sx.input, minHeight: 90, resize: "vertical" }}
              />
            </div>

            <button
              onClick={submitEdit}
              disabled={editSubmitting || deletingTable}
              style={{
                ...sx.primaryBtn,
                opacity: editSubmitting || deletingTable ? 0.7 : 1,
              }}
              className="btn-primary"
            >
              {editSubmitting ? "Saving..." : "Update Table"}
            </button>

            <button
              onClick={() => deleteTableById(editTable)}
              disabled={editSubmitting || deletingTable}
              style={{
                ...sx.dangerBtn,
                opacity: editSubmitting || deletingTable ? 0.7 : 1,
              }}
              className="btn-danger"
            >
              {deletingTable ? "Deleting..." : "Delete Table"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* --------------------------------- styles --------------------------------- */
const sx = {
  page: {
    minHeight: "100vh",
    background: "#f4f6fb",
    fontFamily:
      '"Sora","Plus Jakarta Sans","Segoe UI",Roboto,Arial,sans-serif',
    color: "#0f172a",
  },
  bg: { position: "fixed", inset: 0, pointerEvents: "none" },
  blob: {
    position: "absolute",
    borderRadius: 999,
    filter: "blur(28px)",
    opacity: 0.35,
  },
  b1: { width: 320, height: 320, background: "#19d3ff", left: -60, top: -60 },
  b2: {
    width: 360,
    height: 360,
    background: "transparent",
    right: -80,
    top: "20%",
  },
  b3: {
    width: 300,
    height: 300,
    background: BRAND,
    right: "8%",
    bottom: -120,
    opacity: 0.12,
  },

  pillBtn: {
    border: "1px solid rgba(255,255,255,.45)",
    borderRadius: 999,
    padding: "8px 14px",
    background: "#ffd66e",
    color: "#1a1a1a",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 10px 28px rgba(0,0,0,.18)",
  },
  headerLine: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 10,
  },
  quickActionsRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },

  main: {
    width: "100%",
    margin: "18px auto 30px",
  },

  topCard: {
    width: "100%",
    margin: "0 0 18px",
    padding: 18,
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,.08)",
    borderRadius: 20,
    boxShadow: "0 18px 42px rgba(15,23,42,.12)",
    position: "relative",
    overflow: "hidden",
  },
  statRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  statGroup: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  statPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 12px",
    borderRadius: 999,
    background: "linear-gradient(90deg,#e0f2fe,#ecfeff)",
    border: "1px solid rgba(2,132,199,.25)",
    fontWeight: 800,
    fontSize: 13,
  },
  statPillSecondary: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 12px",
    borderRadius: 999,
    background: "linear-gradient(90deg,#f1f5f9,#ffffff)",
    border: "1px solid rgba(15,23,42,.08)",
    fontWeight: 700,
    fontSize: 13,
    color: "#0f172a",
  },

  searchRow: {
    height: 50,
    display: "grid",
    gridTemplateColumns: "110px 1fr auto",
    alignItems: "center",
    gap: 10,
    marginTop: 14,
    border: "1px solid rgba(15,23,42,.12)",
    borderRadius: 16,
    padding: "0 14px",
    background: "#f8fafc",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,.9)",
  },
  searchInput: {
    outline: "none",
    border: "none",
    fontSize: 14,
    color: "#0f172a",
    background: "transparent",
  },
  refreshBtn: {
    border: "1px solid rgba(15,23,42,.12)",
    background: "#fff",
    borderRadius: 999,
    padding: "6px 12px",
    cursor: "pointer",
    fontWeight: 800,
    boxShadow: "0 8px 18px rgba(15,23,42,.06)",
  },

  gridWrap: { width: "100%", margin: "10px auto 0" },
  sectionsWrap: {
    display: "grid",
    gap: 8,
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 900,
    color: "#0f172a",
  },
  sectionCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 28,
    height: 28,
    borderRadius: 999,
    background: "#e2e8f0",
    color: "#0f172a",
    fontSize: 12,
    fontWeight: 800,
    padding: "0 8px",
  },
  sectionEmpty: {
    border: "1px dashed #cbd5e1",
    background: "#f8fafc",
    borderRadius: 12,
    padding: "10px 12px",
    color: "#64748b",
    fontSize: 13,
  },
  grid: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 220px))",
    justifyContent: "flex-start",
  },
  card: {
    background:
      "linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(135deg, rgba(1,91,154,.35), rgba(1,91,154,.15)) border-box",
    border: "1px solid transparent",
    borderRadius: 18,
    padding: 14,
    boxShadow:
      "0 14px 30px rgba(15,23,42,.10), inset 0 1px 0 rgba(255,255,255,.6)",
    position: "relative",
    overflow: "hidden",
  },
  cardHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: 12,
    background: BRAND,
    color: "#fff",
    display: "grid",
    placeItems: "center",
    fontWeight: 900,
    fontSize: 13,
    boxShadow: "0 8px 18px rgba(1,91,154,.45)",
  },
  cardTitle: { fontWeight: 900, color: "#0f172a", fontSize: 15 },
  cardSubTitle: {
    fontSize: 12,
    color: "#64748b",
    marginTop: 2,
  },
  typeBadge: {
    marginTop: 4,
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    padding: "3px 8px",
    borderRadius: 999,
    background: "rgba(14,165,233,.12)",
    color: "#0369a1",
    fontSize: 11,
    fontWeight: 800,
  },
  metaRow: {
    display: "grid",
    gridTemplateColumns: "0.9fr 1.1fr",
    gap: 10,
    marginTop: 12,
  },
  metaBlock: {
    background: "#F8FAFC",
    border: "1px solid #E2E8F0",
    borderRadius: 14,
    padding: "6px 8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  metaBlockMuted: {
    background: "#F9FAFB",
    border: "1px dashed #E2E8F0",
    borderRadius: 14,
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  metaLabel: { fontSize: 11, color: "#64748b", fontWeight: 800 },
  metaValue: {
    fontSize: 14,
    color: "#0f172a",
    fontWeight: 900,
  },
  metaChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 999,
    background: "rgba(16,185,129,.08)",
    color: "#047857",
    fontSize: 11,
    fontWeight: 700,
  },
  noteBox: {
    marginTop: 10,
    padding: 10,
    background: "#fff8e1",
    border: "1px solid #fde68a",
    borderRadius: 14,
    color: "#92400e",
    fontWeight: 600,
    fontSize: 12,
  },
  noteLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    fontWeight: 800,
    letterSpacing: 0.06,
    marginRight: 6,
    opacity: 0.8,
  },
  cardActions: {
    marginTop: 10,
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
  },
  smallEditBtn: {
    border: "1px solid #cbd5e1",
    background: "#fff",
    borderRadius: 8,
    padding: "4px 8px",
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1.1,
    color: "#0f172a",
    cursor: "pointer",
  },
  smallDeleteBtn: {
    border: "1px solid #ef4444",
    background: "#fff5f5",
    borderRadius: 8,
    padding: "4px 8px",
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1.1,
    color: "#dc2626",
    cursor: "pointer",
  },

  loaderWrap: { display: "grid", placeItems: "center", padding: 40 },
  spinner: {
    width: 28,
    height: 28,
    border: "3px solid #cfe9ff",
    borderTopColor: BRAND,
    borderRadius: "50%",
    animation: "spin .9s linear infinite",
  },
  emptyWrap: { display: "grid", placeItems: "center", padding: 40 },
  emptyCard: {
    textAlign: "center",
    padding: 24,
    borderRadius: 18,
    background: "rgba(255,255,255,.92)",
    border: "1px dashed rgba(148,163,184,.6)",
    boxShadow: "0 10px 26px rgba(15,23,42,.08)",
    maxWidth: 360,
  },
  emptyIcon: { fontSize: 18, marginBottom: 8, fontWeight: 800 },
  emptyTitle: { fontWeight: 800, marginBottom: 4 },
  emptyText: { fontSize: 13, color: "#64748b", marginBottom: 16 },

  sheetBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,.45)",
    display: "grid",
    alignItems: "end",
    zIndex: 50,
  },
  centerBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,.45)",
    display: "grid",
    placeItems: "center",
    zIndex: 50,
    padding: 16,
  },
  sheet: {
    background: "#fff",
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    padding: 16,
    width: "min(560px, 96vw)",
    margin: "0 auto 0",
    boxShadow: "0 -14px 32px rgba(15,23,42,.45)",
    maxHeight: "82vh",
    overflow: "auto",
  },
  centerModal: {
    background: "#fff",
    borderRadius: 18,
    padding: 16,
    width: "min(560px, 96vw)",
    boxShadow: "0 20px 46px rgba(15,23,42,.35)",
    maxHeight: "82vh",
    overflow: "auto",
  },
  grabber: {
    width: 44,
    height: 4,
    borderRadius: 999,
    background: "#CBD5E1",
    margin: "6px auto 12px",
  },
  sheetHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  sheetTitle: { fontSize: 16, fontWeight: 900, color: "#0f172a" },
  sheetHint: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 4,
  },
  iconBtn: {
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    borderRadius: 999,
    padding: "6px 10px",
    cursor: "pointer",
  },

  formGroup: { marginTop: 12 },
  label: {
    fontSize: 13,
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: 6,
    display: "block",
  },
  input: {
    width: "100%",
    border: "1px solid #E2E8F0",
    borderRadius: 12,
    padding: "10px 12px",
    background: "#F8FAFC",
    color: "#0f172a",
    outline: "none",
    fontSize: 14,
  },
  typeRow: {
    display: "inline-flex",
    gap: 8,
    flexWrap: "wrap",
  },
  typeChip: {
    border: "1px solid #CBD5E1",
    background: "#fff",
    borderRadius: 999,
    padding: "8px 12px",
    fontWeight: 700,
    color: "#0f172a",
    cursor: "pointer",
  },
  typeChipActive: {
    borderColor: BRAND,
    background: "rgba(1,91,154,.12)",
    color: BRAND,
  },

  primaryBtn: {
    width: "100%",
    height: 48,
    marginTop: 16,
    borderRadius: 14,
    background: BRAND,
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
    border: `1px solid ${BRAND}`,
    fontSize: 14,
  },
  dangerBtn: {
    width: "100%",
    height: 46,
    marginTop: 12,
    borderRadius: 12,
    border: "1px solid #FCA5A5",
    background: "#FEF2F2",
    color: "#B91C1C",
    fontWeight: 900,
    cursor: "pointer",
    fontSize: 14,
  },
  secondaryBtn: {
    width: "100%",
    height: 46,
    marginTop: 8,
    borderRadius: 12,
    border: "1px solid #E5E7EB",
    background: "#F8FAFC",
    color: "#0f172a",
    fontWeight: 900,
    cursor: "pointer",
    fontSize: 14,
  },

  gridPanel: {
    marginTop: 12,
    background: "#F8FAFC",
    borderRadius: 12,
    padding: 12,
    border: "1px solid #E2E8F0",
  },
  numberGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 10,
  },
  tableTile: {
    borderRadius: 10,
    border: `1px solid ${BRAND}`,
    padding: "12px 0",
    background: "#fff",
    textAlign: "center",
    cursor: "pointer",
  },
  tableTileActive: {
    background: BRAND,
    borderColor: BRAND,
  },
  tableTileDisabled: {
    borderColor: "#CBD5E1",
    background: "#F8FAFC",
    cursor: "not-allowed",
  },
  tableTileText: { color: BRAND, fontWeight: 700, fontSize: 14 },
  tableTileTextActive: { color: "#fff" },
  tableTileTextDisabled: { color: "#94A3B8" },
  showMoreBtn: {
    marginTop: 10,
    border: "1px dashed #94A3B8",
    background: "#fff",
    borderRadius: 10,
    padding: "8px 10px",
    cursor: "pointer",
  },
  editBadge: {
    position: "absolute",
    top: 10,
    right: 10,
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,.15)",
    background: "#fff",
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },

  selectedInfo: {
    marginTop: 12,
    padding: 12,
    background: "#FEF2F2",
    borderRadius: 12,
    border: "1px solid #FEE2E2",
  },
  selectedInfoText: {
    fontSize: 14,
    fontWeight: 700,
    color: "#B91C1C",
    marginBottom: 4,
  },
  selectedInfoSubtext: {
    fontSize: 13,
    color: "#991B1B",
    fontWeight: 600,
  },
};

// keyframes + hover styles for cards/buttons
if (!document.getElementById("add-table-page-styles")) {
  const styleTag = document.createElement("style");
  styleTag.id = "add-table-page-styles";
  styleTag.innerHTML = `
    @keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
    .addtable-hero::before{
      content:"";
      position:absolute;
      inset:-2px;
      background:linear-gradient(135deg, rgba(1,91,154,.25), rgba(1,91,154,.12));
      z-index:0;
      pointer-events:none;
    }
    .addtable-hero > *{
      position:relative;
      z-index:1;
    }
    .table-card{
      transition:transform .18s ease, box-shadow .18s ease, border-color .18s ease;
    }
    .table-card:hover{
      transform:translateY(-4px) scale(1.01);
      box-shadow:0 22px 46px rgba(15,23,42,.18);
      border-color:rgba(1,91,154,.45);
    }
    .btn-pill,
    .btn-primary,
    .btn-secondary,
    .btn-danger,
    .btn-icon{
      transition:transform .12s ease, box-shadow .12s ease, background-color .12s ease, border-color .12s ease, opacity .12s ease;
    }
    .btn-pill:hover,
    .btn-primary:hover,
    .btn-secondary:hover,
    .btn-danger:hover,
    .btn-icon:hover{
      transform:translateY(-1px);
      box-shadow:0 12px 26px rgba(15,23,42,.25);
    }
    .btn-primary:active,
    .btn-secondary:active,
    .btn-pill:active,
    .btn-danger:active,
    .btn-icon:active{
      transform:translateY(0);
      box-shadow:0 4px 14px rgba(15,23,42,.18);
    }
    @media (max-width: 720px){
      .tables-header{
        flex-wrap: wrap;
        gap: 10px;
      }
      .tables-actions{
        width: 100%;
        justify-content: flex-start;
        flex-wrap: wrap;
      }
      .tables-actions .btn-pill{
        flex: 1 1 140px;
        text-align: center;
      }
    }
    .addtable-grid{
      grid-template-columns: repeat(auto-fill, minmax(220px, 220px));
      justify-content: flex-start;
    }
    @media (max-width: 1280px){
      .addtable-grid{
        grid-template-columns: repeat(auto-fill, minmax(220px, 220px));
      }
    }
    @media (max-width: 980px){
      .addtable-grid{
        grid-template-columns: repeat(auto-fill, minmax(220px, 220px));
      }
    }
    @media (max-width: 820px){
      .addtable-main{
        width: 100%;
        padding: 0 12px;
      }
      .addtable-header{
        flex-direction: column;
        align-items: flex-start;
      }
      .addtable-stat{
        width: 100%;
        flex-direction: column;
        align-items: flex-start;
      }
      .addtable-statpill{
        width: 100%;
        justify-content: space-between;
      }
      .addtable-actions{
        width: 100%;
        justify-content: flex-start;
      }
      .addtable-actions .btn-pill{
        flex: 1 1 140px;
        text-align: center;
      }
      .addtable-grid{
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      .addtable-numbergrid{
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }
    }
    @media (max-width: 600px){
      .addtable-hero{
        padding: 14px;
      }
      .addtable-grid{
        grid-template-columns: 1fr;
      }
      .addtable-numbergrid{
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
      .addtable-meta{
        grid-template-columns: 1fr;
      }
    }
    @media (max-width: 480px){
      .addtable-main{
        padding: 0 10px;
      }
      .addtable-grid{
        grid-template-columns: 1fr;
      }
      .addtable-card{
        padding: 12px;
      }
      .addtable-actions .btn-pill{
        flex: 1 1 100%;
      }
    }
  `;
  document.head.appendChild(styleTag);
}

export default AddTablePage;
